import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Chip,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  ArrowRightOutlined,
  PieChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import SimpleBar from "components/third-party/SimpleBar";
import { IMaterialTypeWiseMaterialResult } from "features/libraryDashboard/api/types";

type Props = {
  data: IMaterialTypeWiseMaterialResult[];
};

const PendingRackMaterial = ({ data }: Props) => {
  const [view, setView] = useState("Visual");
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#9d3be3",
    "#FFBB28",
    "#FF8042",
    "#52D726",
    "#FFEC00",
    "#FF7300",
    "#007ED6",
    "#7CDDDD",
    "#64C2A6",
    "#FF2E7E",
  ];

  const pieData = data?.map((d) => ({
    name: d.materialTypeName,
    value: d.pendingRackAssignment,
  }));

  const legendData = pieData?.map((item, index) => ({
    name: item.name,
    color: COLORS[index % COLORS.length],
  }));

  const CustomLegend = () => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 14px",
        alignItems: "center",
        width: "100%",
        maxHeight: 120,
        overflowY: "auto",
        padding: "4px 0",
      }}
    >
      {legendData?.map((item, index) => (
        <div
          key={`legend-item-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: item.color,
            }}
          ></div>
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-word", fontSize: "12px" }}
          >
            {item.name}
          </Typography>
        </div>
      ))}
    </div>
  );

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        sx={{
          p: 2,
          flexWrap: "wrap",
          rowGap: 1,
          "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
        }}
        title="Rack wise material"
        action={
          <Grid container spacing={1} flexWrap="wrap">
            <Grid item>
              <Chip
                icon={<PieChartOutlined />}
                label="Visual"
                color={view === "Visual" ? "primary" : "default"}
                onClick={() => setView("Visual")}
              />
            </Grid>
            <Grid item>
              <Chip
                label="Tabular"
                icon={<TableOutlined />}
                color={view === "Tabular" ? "primary" : "default"}
                onClick={() => setView("Tabular")}
              />
            </Grid>
          </Grid>
        }
      />
      <Divider />
      <CardContent>
        {view === "Visual" && (
          <>
            <Grid
              container
              rowGap={1}
              sx={{
                padding: "10px 16px",
                borderRadius: "8px",
                backgroundColor: "#fff",
                alignItems: "center",
              }}
            >
              {data && data.length > 0 ? (
                <>
                  <Grid item xs={6}>
                    <Typography
                      variant="h5"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#757575",
                        flexWrap: "wrap",
                      }}
                    >
                      Pending Task
                      <ArrowRightOutlined style={{ fontSize: "14px" }} />
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#757575",
                      }}
                    >
                      Rack Assignment
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "red",
                        fontSize: "20px",
                      }}
                    >
                      {data && data.length > 0
                        ? data[0].totalPendingRackAssignment.toLocaleString(
                            "en-IN",
                          )
                        : "No Volume Available"}
                    </Typography>
                  </Grid>
                </>
              ) : (
                <></>
              )}
            </Grid>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <ResponsiveContainer height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={2}
                      dataKey="value"
                      minAngle={20}
                    >
                      {pieData?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomLegend />
              </Grid>
            </Grid>
          </>
        )}

        {view === "Tabular" && (
          <TableContainer sx={{ mt: 0, maxHeight: 340 }}>
            <SimpleBar sx={{ height: 290 }}>
              <Table size="small">
                <TableHead
                  sx={{
                    position: "sticky",
                    top: -1,
                    zIndex: 2,
                  }}
                >
                  <TableRow>
                    <TableCell width={200}>Material Type</TableCell>
                    <TableCell align="right">Material</TableCell>
                    <TableCell align="right">Accessions</TableCell>
                    <TableCell align="right">Pending Rack Assignment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.map((row) => (
                    <TableRow hover key={row.materialTypeID}>
                      <TableCell width={200}>{row.materialTypeName}</TableCell>
                      <TableCell align="right">
                        {row.title.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell align="right">
                        {row.volume.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "red" }}>
                        {row.pendingRackAssignment.toLocaleString("en-IN")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SimpleBar>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingRackMaterial;
